/**
 * EMBED-PROTOCOL v1 (structuurplan §5.2; cross-lane-verzoek issue #49).
 *
 * De namen waarmee een klantpagina en de configurator elkaar spreken: de
 * postMessage-berichttypen van de handshake, de container/element/attribuut-
 * namen, de hoogteklem, de init-timeout en de headers van het cart-relais.
 * Platte constanten, geen logica — de parser en de zenders wonen in de SDK en
 * de runtime, dit bestand is uitsluitend de spelling.
 *
 * Dat "platte constanten" is hier geen stijlregel maar een bouwvoorwaarde: dit
 * bestand wordt meegebundeld in `sdk/v1.js` (§8.2 — IIFE, één bestand, géén
 * dependencies), en `packages/embed/bundel-controle.mjs` faalt op elke
 * overgebleven import. Voeg er dus NOOIT een runtime-import aan toe, en al
 * helemaal geen `zod`. De enige import die hier mag staan is een `export
 * type`/`import type`, want die verdwijnt volledig uit de emit — zie de
 * doorgifte van de stap-9-payload onderaan. `src/__tests__/bundelveilig.test.ts`
 * bewaakt het op de getranspileerde importgraaf.
 *
 * ── WAAROM DE `upgraded:*`-NAMEN HIER STAAN EN GEEN MERKSTRING ZIJN ─────────
 * `apps/portal/tests/merkstrings.test.ts` verbiedt elke losse string met
 * `upgraded` of `mark2form` in `src/` buiten de merk-kernel, en die poort is
 * terecht. Maar deze namen zijn geen merk: ze zijn PROTOCOL. Ze staan in
 * klant-HTML die wij niet kunnen wijzigen (`<div id="upgraded-<slug>">`), in
 * loaders die klanten al geplakt hebben, en in webhook-verifiers die tenants
 * zelf gebouwd hebben op `x-upgraded-signature`. Precies dezelfde reden dat
 * `DESIGN_ID_HEADER_LEGACY` al in `./endpoints` staat: een constante afleiden
 * of overtypen in een app zou de smokkel zijn waar die poort tegen bestaat.
 *
 * ── DE LEGACY-VARIANT BLIJFT ────────────────────────────────────────────────
 * §5.2: "de runtime accepteert `upgraded:*` als alias gedurende de hele
 * v1-levensduur, zodat oude loaders blijven werken." Niets dat werkt mag
 * breken. De `m2f:*`-naam komt ernáást, nooit in de plaats; een zender die
 * beide kan zetten, zet beide, en pas een major mag een legacy-naam laten
 * vallen.
 *
 * ── WAAR ELKE WAARDE VANDAAN KOMT (geverifieerd 21-09-2026) ─────────────────
 * De legacy-kant is byte-gelijk aan wat er vandaag live gesproken wordt,
 * nagelezen in de bron:
 *
 *   - `upgraded:ready` / `upgraded:init` — de loader in het Astro-portaal
 *     (`UpgradedSystems-website/src/pages/api/embed/[slug].js.ts`,
 *     `iframeLoaderScriptV2` + `iframeLoaderScriptCookie`) en de ontvanger
 *     `READY_MESSAGE`/`INIT_MESSAGE` in
 *     `packages/embed/src/embed-session-client.ts`.
 *   - `upgraded:resize` — dezelfde loader (alle drie de iframe-varianten) en
 *     de zender in `packages/configurator-engine/src/use-iframe-resize.ts`.
 *   - `upgraded:design_complete` / `upgraded:request_close` — de zenders
 *     `emitDesignComplete`/`emitRequestClose` in datzelfde bestand. Ze bestaan
 *     maar worden nergens aangeroepen (§5.2 stap 9: aan beide kanten te
 *     bouwen in fase 3); de spelling is er niet minder contract om.
 *   - `"upgraded-"` en `"data-upg"` — de loader:
 *     `document.getElementById('upgraded-'+SLUG)` in élke scriptvariant en
 *     `iframe.setAttribute('data-upg',SLUG)`.
 *   - 200/5000 — de hoogteklem in de loader:
 *     `Math.max(200,Math.min(5000,e.data.height))`.
 *   - 1500 — `INIT_TIMEOUT_MS` in `packages/embed/src/embed-session-client.ts`.
 *   - `x-upgraded-signature`, `x-upgraded-event`, `design.submitted` en
 *     `UpgradedPortalRelay/1.0` — het cart-relais
 *     (`UpgradedSystems-website/src/pages/api/embed/cart.ts`), de code die de
 *     webhook van de maker vandaag daadwerkelijk aanroept. Let op:
 *     `apps/runtime/docs/cart-webhook.md` noemt `UpgradedConfigurator/1.0` —
 *     dat document loopt achter op het relais; wat tenants ontvangen (en dus
 *     het contract) is `UpgradedPortalRelay/1.0`.
 *
 * De `m2f:*`-kant bestaat vandaag nog nergens in code en komt LETTERLIJK uit
 * §5.2 (stappen 2–9) plus het verzoek in issue #49:
 *
 *   - `m2f:ready`, `m2f:init`, `m2f:resize`, `m2f:design-complete`,
 *     `m2f:request-close` — §5.2 stappen 4, 5, 8 en 9. NB: de nieuwe namen
 *     schrijven een KOPPELTEKEN (`design-complete`) waar de legacy-namen een
 *     underscore hebben (`design_complete`); dat is geen slordigheid maar de
 *     spelling van §5.2 naast de spelling van de live code.
 *   - `<mark2form-configurator>` — §5.2 stap 2 (het custom element van de
 *     nieuwe SDK; bestaat pas in fase 3).
 *   - `data-m2f` — §5.2 stap 3.
 *   - `x-m2f-signature` / `x-m2f-event` — dezelfde §5.2-hernoeming die
 *     `DESIGN_ID_HEADER` (`x-m2f-design-id`, `./endpoints`) al volgde,
 *     aangevraagd in issue #49. Niet in code geverifieerd, want er is nog
 *     geen zender.
 *   - `m2f:add-to-cart` / `m2f:cart-result` en het init-veld `winkelwagen` —
 *     de winkelwagenkoppeling (shop-koppelingen, 8-10-2026, WP K1). NIEUW: geen
 *     live loader spreekt ze vandaag. De `upgraded:`-spelling komt alleen mee
 *     omdat elk paar er een heeft; er bestaat geen legacy-code om te bewaren.
 *
 * Het design-id in de header hoort bij ditzelfde relais maar staat NIET hier:
 * `DESIGN_ID_HEADER` en `DESIGN_ID_HEADER_LEGACY` bestonden al in
 * `./endpoints` en een tweede kopie is een bug (§3 principe 3).
 */

/* ══════════════════════════════════════════════════════════════════════════
   DE BERICHTTYPEN VAN DE HANDSHAKE (§5.2 stappen 4–9)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Eén berichttype, in beide spellingen. `m2f` is de naam die v1 draagt,
 * `legacy` is wat elke bestaande loader en embed vandaag spreekt — en blijft
 * spreken gedurende de hele v1-levensduur.
 */
export interface EmbedBerichtPaar {
  readonly m2f: string;
  readonly legacy: string;
}

/**
 * De berichttypen, als paren. Bewust één object en geen zes losse
 * exports: de wortel-barrel van dit package her-exporteert elke submodule, en
 * een kale `READY` of `INIT` in die naamruimte is een aanvaring die op zich
 * laat wachten.
 *
 *   READY            iframe → parent: "ik sta klaar" (§5.2 stap 4)
 *   INIT             parent → iframe: key/mode + parentOrigin (§5.2 stap 5;
 *                    de ontvanger leest ALLEEN `event.origin`, nooit het veld)
 *   RESIZE           iframe → parent: `{height}`, geklemd (§5.2 stap 8)
 *   DESIGN_COMPLETE  iframe → parent: ontwerp afgerond (§5.2 stap 9; zenders
 *                    bestaan, aanroepers zijn fase 3). Het ENIGE bericht met
 *                    een inhoud: `EmbedDesignCompletePayload`, onderaan dit
 *                    bestand doorgegeven uit `./embed-design-complete`
 *   REQUEST_CLOSE    iframe → parent: demonteer mij (§5.2 stap 9; idem)
 *   PLAATSING        iframe → parent: de uitkomst van de sessie voor een embed
 *                    MET plaatsing (koppelplan K05a, nieuw — geen §5.2-stap):
 *                    de opgeloste versie of een weigering. Alleen een iframe
 *                    met een instance-id stuurt hem; zie het blok "PLAATSING
 *                    EN INSTANCE" onderaan. De legacy-naam staat er omdat elk
 *                    paar er een heeft; geen oude loader kent hem.
 *   ADD_TO_CART      iframe → parent: de offerte voor de winkelwagen (WP K1).
 *                    Het token is de bron van de prijs; er zijn geen
 *                    plaatsingsvelden. De pagina zet hem door als CustomEvent
 *                    `m2f:add-to-cart`. Het payload-schema staat in `./winkelwagen`.
 *   CART_RESULT      parent → iframe: de uitkomst van de winkelwagen, door de
 *                    pagina teruggegeven (`WinkelwagenUitkomst`, `./winkelwagen`).
 */
export const EMBED_BERICHTEN = {
  READY: { m2f: "m2f:ready", legacy: "upgraded:ready" },
  INIT: { m2f: "m2f:init", legacy: "upgraded:init" },
  PLAATSING: { m2f: "m2f:placement", legacy: "upgraded:placement" },
  RESIZE: { m2f: "m2f:resize", legacy: "upgraded:resize" },
  DESIGN_COMPLETE: { m2f: "m2f:design-complete", legacy: "upgraded:design_complete" },
  REQUEST_CLOSE: { m2f: "m2f:request-close", legacy: "upgraded:request_close" },
  ADD_TO_CART: { m2f: "m2f:add-to-cart", legacy: "upgraded:add_to_cart" },
  CART_RESULT: { m2f: "m2f:cart-result", legacy: "upgraded:cart_result" },
} as const satisfies Record<string, EmbedBerichtPaar>;

/** De naam van een berichttype: "READY" | "INIT" | … */
export type EmbedBerichtNaam = keyof typeof EMBED_BERICHTEN;

/* ══════════════════════════════════════════════════════════════════════════
   CONTAINER, ELEMENT EN ATTRIBUTEN (§5.2 stappen 2–3)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Het id-voorvoegsel van de legacy-container in klant-HTML:
 * `<div id="upgraded-<slug>">`. Dit staat op pagina's van klanten die wij
 * niet kunnen wijzigen; §5.2 stap 2 houdt hem daarom werkend via de
 * legacy-loader.
 */
export const LEGACY_CONTAINER_PREFIX = "upgraded-";

/**
 * Het custom element van de nieuwe SDK (§5.2 stap 2):
 * `<mark2form-configurator product="…" …>`. Bestaat pas in fase 3; de naam
 * ligt hier alvast vast zodat SDK en runtime hem niet elk zelf spellen.
 */
export const CUSTOM_ELEMENT_NAAM = "mark2form-configurator";

/**
 * Het data-attribuut op de geïnjecteerde `<iframe>` (§5.2 stap 3) — het haakje
 * waarmee een klant er met eigen CSS op selecteert (`iframe[data-m2f]`).
 */
export const DATA_ATTRIBUUT = "data-m2f";

/**
 * De naam die de loader vandaag zet (`iframe.setAttribute('data-upg',SLUG)`).
 * Klant-CSS selecteert erop; hij blijft dus gezet worden naast `data-m2f`.
 */
export const DATA_ATTRIBUUT_LEGACY = "data-upg";

/* ══════════════════════════════════════════════════════════════════════════
   HOOGTEKLEM EN INIT-TIMEOUT (§5.2 stappen 5 en 8)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * De klem op de hoogte uit een RESIZE-bericht, in pixels. De loader vertrouwt
 * de iframe-inhoud niet blind: `Math.max(200, Math.min(5000, height))`.
 * Ondergrens 200 houdt de embed zichtbaar bij een kapotte meting; bovengrens
 * 5000 voorkomt dat een gek gemeten (of vijandig) bericht de klantpagina
 * kilometers lang maakt.
 */
export const HOOGTE_MIN_PX = 200;
export const HOOGTE_MAX_PX = 5000;

/**
 * Hoe lang de embed na zijn READY op de INIT van de loader wacht (§5.2 stap
 * 5). Lang genoeg voor een loader die nog aan het laden is, kort genoeg om
 * een bezoeker niet te laten staren bij een container die de handshake niet
 * kent; daarna is `embed-init-timeout` de nette eindstand.
 */
export const INIT_TIMEOUT_MS = 1500;

/* ══════════════════════════════════════════════════════════════════════════
   HET CART-RELAIS — de headers naar de webhook van de maker
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * De header met de HMAC-SHA256 (hex) van de body, getekend met het
 * `webhookSecret` van de tenant — de naam die v1 draagt.
 */
export const CART_SIGNATURE_HEADER = "x-m2f-signature";

/**
 * Wat élke bestaande cart-webhook-tenant vandaag verifieert
 * (`apps/runtime/docs/cart-webhook.md` schrijft het ze letterlijk voor, met
 * codevoorbeelden). Deze header MOET meegestuurd blijven worden; de nieuwe
 * naam komt ernaast, niet in de plaats.
 */
export const CART_SIGNATURE_HEADER_LEGACY = "x-upgraded-signature";

/** De header met het eventtype van de aflevering — de naam die v1 draagt. */
export const CART_EVENT_HEADER = "x-m2f-event";

/** De eventtype-header die het relais vandaag stuurt. Blijft, zie boven. */
export const CART_EVENT_HEADER_LEGACY = "x-upgraded-event";

/**
 * De enige waarde die het cart-relais vandaag in de event-header zet. (De
 * `x-upgraded-event` van `@mark2form/mail/notify-maker` is een ÁNDER contract
 * — geen HMAC, andere user-agent, variabele eventtypes — en hoort hier niet.)
 */
export const CART_EVENT_WAARDE = "design.submitted";

/**
 * De `user-agent` van het cart-relais, letterlijk zoals de live relay-code hem
 * stuurt. NB: `cart-webhook.md` documenteert `UpgradedConfigurator/1.0`; dat
 * is nooit wat er over de lijn ging — tenants die op de user-agent filteren
 * (het document noemt een major-bump ervan als versiesignaal) zien déze.
 */
export const CART_RELAY_USER_AGENT = "UpgradedPortalRelay/1.0";

/* ══════════════════════════════════════════════════════════════════════════
   DE PAYLOAD VAN STAP 9 — hier alleen het TYPE
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * `design-complete` draagt als enige van de §5.2-berichten een inhoud die
 * ergens over gaat: `{ design_id, email?, total_price? }` (issue #56,
 * eigenaarsbesluit van 21-9 over de eenheid). Die vorm staat in
 * **`./embed-design-complete`**, samen met de regels die eraan vastzitten —
 * decimalen en afronding, dat dit veld NOOIT een factuur onderbouwt, en dat
 * `email` een persoonsgegeven is dat met `targetOrigin: "*"` op de pagina van
 * een klant belandt. Lees dat docblok voordat je het veld vult.
 *
 * Hier staat alleen de TYPE-doorgifte, en dat is geen netheid maar een
 * bouwvoorwaarde. Dit bestand gaat mee in `sdk/v1.js` (§8.2: één bestand, geen
 * dependencies); het payloadschema is zod, en zod hoort niet op de pagina van
 * een klant. Een `export type` verdwijnt volledig uit de emit, dus de bundel
 * merkt niets van deze twee regels — en de SDK hoeft de velden niet zelf te
 * spellen.
 *
 * **Maak hier nooit een gewone `export … from`.** Dan staat zod in de bundel,
 * faalt `packages/embed/bundel-controle.mjs`, en is de regel pas zichtbaar op
 * het moment dat iemand probeert te releasen. `src/__tests__/bundelveilig.test.ts`
 * vangt het eerder.
 */

/**
 * ── DE WEERGAVE VAN EEN EMBED-SESSIE (#125, besluit Pascal 23-9-2026) ──────
 * Het optionele veld `weergave` in de body van `POST /api/v1/embed/session`
 * (de exchange). Het zegt WAAR de configurator draait:
 *
 *  - `"test"`  — de testweergave in het portaal zelf. De runtime zet dit
 *                ALLEEN op de studioroute die niet in een frame op een andere
 *                site te laden is (`frame-ancestors` beperkt tot het portaal).
 *  - `"embed"` — de configurator op de eigen website van de klant.
 *
 * Waarom het bestaat: tijdens de proef mag de klant alleen zelf testen en
 * gaat de configurator niet live op zijn site (`PROEF.liveOpEigenSite` in
 * `@mark2form/catalog/proef`). Aan `parentOrigin` alleen is dat niet te zien:
 * een v1-iframe met `?key=` en de testweergave sturen allebei geen
 * `parentOrigin`. Met dit veld laat de exchange tijdens de proef alleen
 * `weergave: "test"` door.
 *
 * AFWEZIG is geen van beide: de exchange valt dan terug op wat hij zonder het
 * veld deed (tijdens de proef weigert hij `parentOrigin`, na de proef alles).
 * Een onbekende waarde is geen `"test"`. De runtime (L3) zet het veld pas in
 * zijn eigen ronde; het staat hier alvast zodat daar geen contractwijziging
 * meer bij hoeft. Platte constanten, zonder zod: dit bestand gaat mee in de
 * SDK-bundel (zie het kopblok).
 */
export const EMBED_WEERGAVEN = ["test", "embed"] as const;

/** `"test"` (portaal-testweergave) of `"embed"` (de eigen site van de klant). */
export type EmbedWeergave = (typeof EMBED_WEERGAVEN)[number];

/** Is dit een bekende weergave? Alles anders telt als afwezig. */
export function isEmbedWeergave(waarde: unknown): waarde is EmbedWeergave {
  return typeof waarde === "string" && (EMBED_WEERGAVEN as readonly string[]).includes(waarde);
}

/* ══════════════════════════════════════════════════════════════════════════
   DE WINKELWAGEN-MODUS (shop-koppelingen, WP K1)
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Het attribuut `cart` van `<mark2form-configurator>` en het init-veld
 * `winkelwagen`: de klantpagina belooft dat zij de winkelwagen afhandelt.
 * `"host"` is de enige waarde: de pagina zet het artikel zelf in haar eigen
 * winkelwagen, met de prijs uit het ondertekende offertetoken.
 *
 * Een init zonder het veld, of met een waarde die we niet kennen, betekent
 * "geen winkelwagen-belofte": de knop In winkelwagen verschijnt dan niet. Een
 * onbekende waarde wordt GENEGEERD en niet geweigerd (§8.2: een nieuwere
 * iframe of pagina mag meer sturen dan een oudere `v1.js` kent).
 *
 * Zod-vrij, net als `EMBED_WEERGAVEN`: dit bestand gaat mee in `sdk/v1.js`. Het
 * zod-schema `WinkelwagenModusSchema` staat in `./winkelwagen` en leest deze
 * constante.
 */
export const WINKELWAGEN_MODI = ["host"] as const;

/** `"host"`: de pagina voegt het artikel zelf toe aan haar winkelwagen. */
export type WinkelwagenModus = (typeof WINKELWAGEN_MODI)[number];

/** Is dit een bekende winkelwagen-modus? Alles anders telt als afwezig. */
export function isWinkelwagenModus(waarde: unknown): waarde is WinkelwagenModus {
  return typeof waarde === "string" && (WINKELWAGEN_MODI as readonly string[]).includes(waarde);
}

/**
 * De grenzen van de winkelwagenberichten (`m2f:add-to-cart`, `m2f:cart-result`).
 * Ze staan HIER, zod-vrij, omdat zowel de zod-schema's in `./winkelwagen` als de
 * handgeschreven lezer in `sdk/v1.js` ze nodig hebben; één bron, geen kopie.
 */
export const OFFERTE_ID_PATROON = /^q_[a-z0-9]{24}$/;
export const OFFERTE_TOKEN_MAX_LENGTE = 4096;
export const OFFERTE_TOKEN_MIN_LENGTE = 20;
export const WINKELWAGEN_TITEL_MAX = 120;
export const WINKELWAGEN_REGELS_MAX = 8;
export const WINKELWAGEN_LABEL_MAX = 40;
export const WINKELWAGEN_WAARDE_MAX = 120;
export const WINKELWAGEN_MELDING_MAX = 200;
/** Zo lang wacht de configurator op `m2f:cart-result`; daarna `geen-antwoord`. */
export const WINKELWAGEN_ANTWOORD_TIMEOUT_MS = 15_000;
export const WINKELWAGEN_FOUTCODES = [
  "ongeldig-token",
  "verlopen",
  "niet-gekoppeld",
  "winkel-fout",
  "geen-antwoord",
] as const;
export type WinkelwagenFoutcode = (typeof WINKELWAGEN_FOUTCODES)[number];

/** Is dit een offerte-id (`q_` + 24 kleine letters/cijfers)? */
export function isOfferteId(waarde: unknown): waarde is string {
  return typeof waarde === "string" && OFFERTE_ID_PATROON.test(waarde);
}

/* ══════════════════════════════════════════════════════════════════════════
   PLAATSING EN INSTANCE (koppelplan K05a) — optioneel en additief
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * ── WAT DIT BLOK DOET ───────────────────────────────────────────────────────
 * Een embed kan bij een opgeslagen PLAATSING horen (`PlaatsingV1`,
 * `./plaatsing`, K01). Dan reizen er optionele velden mee in de handshake: de
 * publieke plaatsing-id, een instance-id die dit ene element op de pagina
 * aanwijst, en, ná de sessie-uitwisseling, de versie die het portaal voor deze
 * sessie heeft opgelost of een weigering. Ontwerp:
 * `docs/plannen/2026-09-28-koppelingen/k05-runtime.md`.
 *
 * Een bericht ZONDER deze velden is precies het bericht van vandaag en blijft
 * geldig. Een snippet zonder `placement` zet ze nooit.
 *
 * ── WAAROM HIER EEN ZOD-VRIJE PROJECTIE VAN DE TWEE PATRONEN STAAT ──────────
 * De bron van beide id-patronen is `./plaatsing` (`PLAATSING_PUBLIEKE_ID_PATROON`,
 * `PLAATSING_INSTANCE_ID_PATROON`, met hun zod-schema's). Dat bestand laadt
 * zod, en dit bestand gaat mee in `sdk/v1.js`: geen zod, geen import (zie het
 * kopblok, `bundelveilig.test.ts`). Het custom element moet het
 * `placement`-attribuut toch strikt keuren, en de zenders in de iframe moeten
 * de instance uit hun URL keuren zonder zod. Daarom staan de twee patronen hier
 * nog één keer, als PROJECTIE, met een eigen naam (anders botsen ze in de
 * wortel-barrel). `src/__tests__/embed-plaatsing.test.ts` houdt ze byte-gelijk
 * aan de bron (`source` en `flags`); wie de bron wijzigt en dit vergeet, ziet
 * die test vallen. Dezelfde constructie als `./endpoint-paden` naast
 * `./endpoints`.
 */

/** Projectie van `PLAATSING_PUBLIEKE_ID_PATROON` (`./plaatsing`): `pl_` + 16–32 kleine letters/cijfers. */
export const EMBED_PLAATSING_ID_PATROON = /^pl_[a-z0-9]{16,32}$/;

/**
 * Projectie van `PLAATSING_INSTANCE_ID_PATROON` (`./plaatsing`): 8–64 tekens
 * uit `A–Z a–z 0–9 _ -`. Het element maakt de id per iframe met
 * `crypto.getRandomValues`. Hij is een CORRELATIE en geen geheim: hij staat in
 * `iframe.src` en is dus leesbaar voor elk script op de klantpagina. De
 * herkomst van een bericht bewijzen doen `event.source` en de exacte origin;
 * de instance-id zegt alleen welk element en welke sessie het zijn.
 */
export const EMBED_INSTANCE_ID_PATROON = /^[A-Za-z0-9_-]{8,64}$/;

/** Voldoet dit aan het plaatsing-id-patroon? Geen trim: strikt. */
export function isPlaatsingPubliekeId(waarde: unknown): waarde is string {
  return typeof waarde === "string" && EMBED_PLAATSING_ID_PATROON.test(waarde);
}

/** Voldoet dit aan het instance-id-patroon? */
export function isPlaatsingInstanceId(waarde: unknown): waarde is string {
  return typeof waarde === "string" && EMBED_INSTANCE_ID_PATROON.test(waarde);
}

/**
 * Een plaatsingsversie: een geheel getal ≥ 1. De zod-vrije PROJECTIE van
 * `PlaatsingVersieSchema` uit `./plaatsing` (daar staat de regel, met
 * `isPlaatsingVersie`), met een eigen naam om dezelfde reden als de patronen
 * hierboven. `embed-plaatsing.test.ts` houdt de twee gelijk en toetst dat de
 * regel nergens anders gespeld wordt.
 */
export function isEmbedPlaatsingVersie(waarde: unknown): waarde is number {
  return typeof waarde === "number" && Number.isSafeInteger(waarde) && waarde >= 1;
}

/**
 * De queryparameters op `iframe.src` (SDK → runtime). ALLEEN gezet als het
 * element een `placement` heeft; zonder blijft de URL van vandaag
 * byte-gelijk. Ze staan in de URL omdat de iframe ze nodig heeft vóór de
 * eerste zin van de handshake: zijn READY zegt de instance al terug, en de SDK
 * stuurt pas een INIT (met de sleutel) als die klopt. De INIT zegt ze op zijn
 * beurt terug, zodat de iframe weet dat het antwoord voor hem is.
 */
export const EMBED_PLAATSING_QUERY = {
  PLAATSING: "placement",
  INSTANCE: "instance",
} as const;

/**
 * De optionele velden op de draad, in de spelling van `IntakeEnvelopeV1`
 * (`plaatsing_id`, `plaatsing_versie`, `instance_id`), plus de weigering.
 */
export const EMBED_PLAATSING_VELDEN = [
  "instance_id",
  "plaatsing_id",
  "plaatsing_versie",
  "plaatsing_weigering",
] as const;
export type EmbedPlaatsingVeld = (typeof EMBED_PLAATSING_VELDEN)[number];

/**
 * Welk bericht welk veld mag dragen. Een veld dat hier niet bij een bericht
 * staat, leest de ontvanger niet (en weigert hij ook niet: §8.2, een nieuwere
 * iframe mag meer sturen dan een oudere `v1.js` kent).
 *
 *   READY      iframe → parent  instance + plaatsing uit de eigen URL (echo)
 *   INIT       parent → iframe  dezelfde instance + plaatsing (echo terug)
 *   PLAATSING  iframe → parent  de uitkomst van de sessie-uitwisseling: óf de
 *                               versie die het portaal oploste, óf een weigering
 *   RESIZE, DESIGN_COMPLETE, REQUEST_CLOSE, ADD_TO_CART
 *              iframe → parent  alleen de instance
 *   CART_RESULT (WP K1)
 *              parent → iframe; correleert via origin, source en offerte_id
 */
export const EMBED_PLAATSING_VELDEN_PER_BERICHT = {
  READY: ["instance_id", "plaatsing_id"],
  INIT: ["instance_id", "plaatsing_id"],
  PLAATSING: ["instance_id", "plaatsing_id", "plaatsing_versie", "plaatsing_weigering"],
  RESIZE: ["instance_id"],
  DESIGN_COMPLETE: ["instance_id"],
  REQUEST_CLOSE: ["instance_id"],
  ADD_TO_CART: ["instance_id"],
  CART_RESULT: [],
} as const satisfies { readonly [N in EmbedBerichtNaam]: readonly EmbedPlaatsingVeld[] };

/**
 * Waarom een plaatsing niet draait, zoals de BEZOEKER en de klantpagina het te
 * zien krijgen. Bewust één neutrale code: of een id niet bestaat, gepauzeerd
 * is of niet bij dit domein hoort, zegt de publieke kant niet (dat zou een
 * id-scanner helpen). De precieze reden (`ResolutieFout` van K03) blijft in
 * het portaal. K03 mag hier codes aan toevoegen (additief); een ontvanger die
 * een code niet kent, behandelt hem als deze.
 */
export const EMBED_PLAATSING_WEIGERCODES = ["plaatsing-niet-beschikbaar"] as const;
export type EmbedPlaatsingWeigercode = (typeof EMBED_PLAATSING_WEIGERCODES)[number];

/** De standaardweigering; ook wat een ontvanger van een onbekende code maakt. */
export const PLAATSING_NIET_BESCHIKBAAR = "plaatsing-niet-beschikbaar" as const satisfies EmbedPlaatsingWeigercode;

export function isEmbedPlaatsingWeigercode(waarde: unknown): waarde is EmbedPlaatsingWeigercode {
  return (
    typeof waarde === "string" && (EMBED_PLAATSING_WEIGERCODES as readonly string[]).includes(waarde)
  );
}

/**
 * HULPCODES (M16/K31): een korte, merkloze code die de bezoeker naast een
 * foutmelding ziet en die de SDK in de console zet. Het portaal legt hem uit
 * (Koppelen → websiteblok → "Werkt het niet?"). De sleutel is de oorzaak zoals
 * de bron hem kent: een SDK-foutcode, een publieke weigercode van de exchange,
 * of een stand van de legacy-loader. Onbekend → E100.
 */
export const EMBED_HULPCODES = {
  "embed-init-timeout": "E101",
  "embed-missing-product": "E102",
  "embed-missing-credential": "E103",
  "embed-invalid-placement": "E104",
  "plaatsing-niet-beschikbaar": "E105",
  "origin-not-allowed": "E106",
  "sleutel-niet-actief": "E107",
  "sleutel-andere-software": "E108",
  "proef-niet-live": "E109",
  "proef-voorbij": "E110",
  "niet-bereikbaar": "E111",
  "demo-verlopen": "E112",
} as const;
export type EmbedHulpOorzaak = keyof typeof EMBED_HULPCODES;
export type EmbedHulpcode = (typeof EMBED_HULPCODES)[EmbedHulpOorzaak] | "E100";
export const EMBED_HULPCODE_ONBEKEND = "E100" as const;
export function embedHulpcode(oorzaak: string): EmbedHulpcode {
  return Object.prototype.hasOwnProperty.call(EMBED_HULPCODES, oorzaak)
    ? EMBED_HULPCODES[oorzaak as EmbedHulpOorzaak] : EMBED_HULPCODE_ONBEKEND;
}
/** Alle codes, voor de uitlegtabel in het portaal. */
export const EMBED_HULPCODE_LIJST: readonly EmbedHulpcode[] = [EMBED_HULPCODE_ONBEKEND, ...Object.values(EMBED_HULPCODES)];

/**
 * De bevestiging van een plaatsing in de draadspelling: de id en de actieve
 * versie van `PlaatsingV1` (`plaatsing_id`, `versie`), zoals het portaal ze
 * bij de sessie-uitwisseling oploste. Geen tweede plaatsingstype: dit is de
 * projectie die het sessieantwoord en het PLAATSING-bericht dragen.
 */
export interface EmbedPlaatsingBevestiging {
  readonly plaatsing_id: string;
  readonly plaatsing_versie: number;
}

/** De vier velden zoals een ontvanger ze na het keuren heeft. */
export interface EmbedPlaatsingVelden {
  readonly instance_id?: string;
  readonly plaatsing_id?: string;
  readonly plaatsing_versie?: number;
  readonly plaatsing_weigering?: string;
}

/**
 * De samenhangregels, één keer: de zod-schema's in `./plaatsing` en de parser
 * in de SDK roepen allebei deze functie aan. `null` = in orde, anders de
 * reden.
 *
 *   - geen plaatsing zonder instance (anders is het antwoord aan niemand);
 *   - geen versie zonder plaatsing-id;
 *   - een weigering hoort bij een instance en sluit een versie uit;
 *   - een PLAATSING-bericht draagt een instance en precies één uitkomst:
 *     óf een versie, óf een weigering.
 */
export function plaatsingVeldenFout(naam: EmbedBerichtNaam, velden: EmbedPlaatsingVelden): string | null {
  if (velden.plaatsing_id !== undefined && velden.instance_id === undefined) {
    return "plaatsing_id zonder instance_id";
  }
  if (velden.plaatsing_versie !== undefined && velden.plaatsing_id === undefined) {
    return "plaatsing_versie zonder plaatsing_id";
  }
  if (velden.plaatsing_weigering !== undefined && velden.instance_id === undefined) {
    return "plaatsing_weigering zonder instance_id";
  }
  if (velden.plaatsing_weigering !== undefined && velden.plaatsing_versie !== undefined) {
    return "plaatsing_weigering naast plaatsing_versie";
  }
  if (naam === "PLAATSING") {
    if (velden.instance_id === undefined) return "PLAATSING zonder instance_id";
    if (velden.plaatsing_versie === undefined && velden.plaatsing_weigering === undefined) {
      return "PLAATSING zonder versie en zonder weigering";
    }
  }
  return null;
}

/**
 * De instance-id uit de zoekparameters van de iframe-URL, of `null`. Voor de
 * zenders IN de iframe: één iframe is één document en één instance, dus de
 * eigen URL is de bron. Structureel getypt (geen `URLSearchParams`-type), zodat
 * dit bestand geen DOM-lib nodig heeft.
 */
export function embedInstanceUit(zoek: { get(naam: string): string | null }): string | null {
  const waarde = zoek.get(EMBED_PLAATSING_QUERY.INSTANCE);
  return isPlaatsingInstanceId(waarde) ? waarde : null;
}

/**
 * De plaatsing-id uit de zoekparameters van de iframe-URL, of `null`. Een
 * `placement` die er staat maar niet deugt, is ook `null`; de aanroeper weet
 * dan via `zoek.has(…)` dat er een plaatsing gevraagd is die niet kan draaien.
 */
export function embedPlaatsingUit(zoek: { get(naam: string): string | null }): string | null {
  const waarde = zoek.get(EMBED_PLAATSING_QUERY.PLAATSING);
  return isPlaatsingPubliekeId(waarde) ? waarde : null;
}

export type { EmbedBedrag, EmbedDesignCompletePayload } from "./embed-design-complete";
